/**
 * Supabase storage adapter for cookie consent records.
 *
 * Required Supabase table (see supabase-schema.sql):
 *   cookie_consents (user_id, domain, consent_type, categories, ip_country, created_at, updated_at)
 *
 * Row-Level Security tip: enable RLS with a policy that allows INSERT/UPDATE/SELECT
 * where user_id = the anon user's value (no auth needed — user_id is a client fingerprint).
 */
export class SupabaseStorage {
  /**
   * @param {string} supabaseUrl  – e.g. https://xxxx.supabase.co
   * @param {string} supabaseKey  – anon public key
   */
  constructor(supabaseUrl, supabaseKey) {
    this.supabaseUrl = supabaseUrl;
    this.supabaseKey = supabaseKey;
    this.client = null;
    this.table = 'cookie_consents';
    this.ready = false;
  }

  /**
   * Initialize the Supabase client.
   * Works whether @supabase/supabase-js is a bundled dep or loaded via CDN.
   * @returns {Promise<boolean>}
   */
  async init() {
    try {
      let createClient;

      // 1. Already on the page (CDN global)
      if (typeof window !== 'undefined' && window.supabase?.createClient) {
        createClient = window.supabase.createClient;
      } else {
        // 2. Try to import from npm bundle or CDN ESM
        try {
          const mod = await import('@supabase/supabase-js');
          createClient = mod.createClient;
        } catch {
          const mod = await import(
            'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2/+esm'
          );
          createClient = mod.createClient;
        }
      }

      this.client = createClient(this.supabaseUrl, this.supabaseKey, {
        auth: { persistSession: false },
      });

      this.ready = true;
      return true;
    } catch (err) {
      console.warn('[VCCookieConsent] Supabase unavailable — localStorage only.', err);
      return false;
    }
  }

  /**
   * Upsert a consent record.
   * @param {string} userId    – anonymous fingerprint
   * @param {string} domain    – window.location.hostname
   * @param {object} data      – { type, categories, country }
   * @returns {Promise<boolean>}
   */
  async save(userId, domain, data) {
    if (!this.ready || !this.client) return false;

    try {
      const { error } = await this.client.from(this.table).upsert(
        {
          user_id: userId,
          domain,
          consent_type: data.type,
          categories: data.categories,
          ip_country: data.country || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,domain' }
      );

      if (error) throw error;
      return true;
    } catch (err) {
      console.warn('[VCCookieConsent] Failed to save consent to Supabase.', err);
      return false;
    }
  }

  /**
   * Fetch the latest consent record for a user+domain.
   * @param {string} userId
   * @param {string} domain
   * @returns {Promise<object|null>}
   */
  async load(userId, domain) {
    if (!this.ready || !this.client) return null;

    try {
      const { data, error } = await this.client
        .from(this.table)
        .select('consent_type, categories, ip_country, updated_at')
        .eq('user_id', userId)
        .eq('domain', domain)
        .maybeSingle();

      if (error) throw error;
      return data || null;
    } catch (err) {
      console.warn('[VCCookieConsent] Failed to load consent from Supabase.', err);
      return null;
    }
  }
}
